import { api } from 'common';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import { ISettingsType } from 'common/src/types';
import Typography from '@mui/material/Typography';
import { useSelector, useDispatch } from "react-redux";
import { useAlert } from 'components/alert/AlertContext';
import CircularLoading from "../../components/CircularLoading";
import FormControlLabel from '@mui/material/FormControlLabel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Autocomplete, Box, Button, Alert, Paper } from '@mui/material';

const GeneralSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const settings = useSelector((state) => state.settingsdata?.settings);
  const [tempSettings, setTempSettings] = useState<Partial<ISettingsType>>();
  const [selectedAdditionalPayments, setSelectedAdditionalPayments] = useState<any[]>([]);

  const additionalPaymentOptions = [
    { value: 'driver_pix', label: t('driver_pix' as any) },
    { value: 'driver_debit_card', label: t('driver_debit_card' as any) },
    { value: 'driver_credit_card', label: t('driver_credit_card' as any) },
  ];

  const { editSettings } = api;

  useEffect(() => {
    if (settings) {
      setTempSettings(settings);

      if (settings?.additional_payments) {
        try {
          const savedPayments = Array.isArray(settings.additional_payments)
            ? settings.additional_payments
            : JSON.parse(settings.additional_payments || '[]');

          const validSavedPayments = Array.isArray(savedPayments) ? savedPayments : [];
          const selectedOptions = additionalPaymentOptions.filter(option =>
            validSavedPayments.includes(option.value)
          );
          setSelectedAdditionalPayments(selectedOptions || []);
        } catch (error) {
          console.error('Error parsing additional_payments:', error);
          setSelectedAdditionalPayments([]);
        }
      }
    }
  }, [settings]);

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setTempSettings({
      ...tempSettings,
      [name]: checked
    });
  };

  const handleBonusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      setTempSettings({
        ...tempSettings,
        bonus: '' as any
      });
      return;
    }

    const value = parseFloat(inputValue);
    if (!isNaN(value) && value >= 0) {
      setTempSettings({
        ...tempSettings,
        bonus: value
      });
    }
  };

  const handleDriverRadius = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setTempSettings({
      ...tempSettings,
      driverRadius: value >= 0 ? value : 0
    });
  };

  const handleMaxPassengerNegativeBalance = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setTempSettings({
      ...tempSettings,
      max_passenger_negative_balance: value >= 0 ? value : 0
    });
  };

  const handleAdditionalPaymentsChange = (event: any, newValue: any) => {
    const safeNewValue = Array.isArray(newValue) ? newValue : [];
    setSelectedAdditionalPayments(safeNewValue);

    const paymentValues = safeNewValue.map(item => item.value);
    setTempSettings({
      ...tempSettings,
      additional_payments: JSON.stringify(paymentValues) as any
    });
  };

  const handleNegativeBalance = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setTempSettings({
      ...tempSettings,
      maxNegativeBalance: value >= 0 ? String(value) : "0"
    });
  };

  const handleDriverPixPaymentOnly = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    if (checked) {
      const driverPixOption = additionalPaymentOptions.find(opt => opt.value === 'driver_pix');

      setTempSettings({
        ...tempSettings,
        disable_cash: true,
        disable_online: true,
        disable_wallet: true,
        driver_pix_payment_only: true,
        additional_payments: ['driver_pix']
      });

      setSelectedAdditionalPayments(driverPixOption ? [driverPixOption] : []);

    } else {
      setTempSettings({
        ...tempSettings,
        driver_pix_payment_only: false,
      });
    }
  };

  const handleSave = () => {
    try {
      if (tempSettings) {
        dispatch(editSettings(tempSettings));
        showAlert({ type: "SUCCESS", title: t("success"), message: t("SAVED_SUCCESSFULLY") })
      }
    } catch (e) {
      showAlert({ type: "ERROR", title: t("error"), message: JSON.stringify(e) ?? t("no_details_error") })
    }
  };

  const handleArrivalRadius = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setTempSettings({
      ...tempSettings,
      arrival_radius: value >= 0 ? value : 0
    });
  };

  const handleTrackingMinDistance = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setTempSettings({
      ...tempSettings,
      tracking_min_distance: value >= 0 ? value : 0
    });
  };

  const handleCancelDistanceThreshold = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setTempSettings({
      ...tempSettings,
      cancel_distance_threshold: value >= 0 ? value : 0
    });
  };

  const isDriverPixPaymentOnly = tempSettings?.driver_pix_payment_only === true;

  if (!settings || !tempSettings) {
    return <CircularLoading />;
  }

  return (
    <Grid item xs={12} sm={12}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={6}>


          <Typography component="h1" variant="h5">
            {t('PAYMENT_METHOD_CONFIGURED')}
          </Typography>

          <Paper
            elevation={3}
            sx={{ my: 3, p: 2, borderRadius: 2 }}
          >
            <FormControlLabel
              control={
                <Switch
                  color="warning"
                  name="driver_pix_payment_only"
                  checked={isDriverPixPaymentOnly}
                  onChange={handleDriverPixPaymentOnly}
                />
              }
              label={
                <Typography variant="h6" fontWeight="bold" color={isDriverPixPaymentOnly ? 'warning.main' : 'text.primary'}>
                  {t('DRIVER_PIX_PAYMENT_ONLY')}
                </Typography>
              }
            />

            {isDriverPixPaymentOnly && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{ mt: 2, mb: 1 }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {t('DRIVER_PIX_PAYMENT_ONLY_WARNING')}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  • {t('driver_will_receive_payment_directly')}
                  <br />
                  • {t('driver_must_confirm_payment_received')}
                  <br />
                  • {t('payment_confirmation_required_in_app')}
                </Typography>
              </Alert>
            )}

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              {isDriverPixPaymentOnly
                ? t('DRIVER_PIX_PAYMENT_ONLY_ACTIVE_DESC')
                : t('DRIVER_PIX_PAYMENT_ONLY_DESC')}
            </Typography>
          </Paper>


          <Grid container spacing={2} padding={2.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.disable_online || false}
                  onChange={handleSwitchChange}
                  name="disable_online"
                  color="primary"
                  disabled={isDriverPixPaymentOnly}
                />
              }
              label={
                <Box>
                  {t('disable_online')}
                  {isDriverPixPaymentOnly && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {t('disabled_by_driver_pix_mode')}
                    </Typography>
                  )}
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.disable_wallet || false}
                  onChange={handleSwitchChange}
                  name="disable_wallet"
                  color="primary"
                  disabled={isDriverPixPaymentOnly}
                />
              }
              label={
                <Box>
                  {t('disable_wallet' as any)}
                  {isDriverPixPaymentOnly && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {t('disabled_by_driver_pix_mode')}
                    </Typography>
                  )}
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.disable_cash || false}
                  onChange={handleSwitchChange}
                  name="disable_cash"
                  color="primary"
                  disabled={isDriverPixPaymentOnly}
                />
              }
              label={
                <Box>
                  {t('disable_cash')}
                  {isDriverPixPaymentOnly && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {t('disabled_by_driver_pix_mode')}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Grid>

          <Autocomplete
            multiple
            id="additional_payments"
            options={additionalPaymentOptions}
            getOptionLabel={(option) => option?.label || ''}
            value={selectedAdditionalPayments}
            onChange={handleAdditionalPaymentsChange}
            disabled={isDriverPixPaymentOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t('select_additional_payments' as any)}
                placeholder={t('select_additional_payments' as any)}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option?.label || ''}
                {isDriverPixPaymentOnly && option.value === 'driver_pix' && (
                  <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                    ({t('selected_automatically')})
                  </Typography>
                )}
              </Box>
            )}
            isOptionEqualToValue={(option, value) =>
              option?.value === value?.value
            }
          />
          <Typography component="h1" variant="h5" style={{ marginTop: '15px' }}>
            {t('security_title')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.otp_secure}
                onChange={handleSwitchChange}
                name="otp_secure"
                color="primary"
              />
            }
            label={t('settings_label3')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.driver_approval || false}
                onChange={handleSwitchChange}
                name="driver_approval"
                color="primary"
              />
            }
            label={t('settings_label4')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.carApproval || false}
                onChange={handleSwitchChange}
                name="carApproval"
                color="primary"
              />
            }
            label={t('settings_label5')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.imageIdApproval || false}
                onChange={handleSwitchChange}
                name="imageIdApproval"
                color="primary"
              />
            }
            label={t('settings_label6')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.passengerIdentification || false}
                name="passengerIdentification"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('PASSENGER_IDENTIFICATION')}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Typography component="h1" variant="h5" style={{ marginTop: '13px' }}>
                {t('referral_bonus')}
              </Typography>
              <TextField
                fullWidth
                id="bonus"
                name="bonus"
                type="number"
                margin="normal"
                variant="outlined"
                autoComplete="bonus"
                label={t('referral_bonus')}
                onChange={handleBonusChange}
                value={tempSettings.bonus ?? ''}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
          </Grid>
          <Typography component="h1" variant="h5" style={{ marginTop: '15px' }}>
            {t('GPS_SETTINGS')}
          </Typography>

          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            id="arrival_radius"
            name="arrival_radius"
            onChange={handleArrivalRadius}
            label={t("ARRIVAL_RADIUS_METERS")}
            value={tempSettings.arrival_radius || ''}
          />

          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            id="tracking_min_distance"
            name="tracking_min_distance"
            onChange={handleTrackingMinDistance}
            label={t("MIN_DISTANCE_TO_SEND_TRACKING_METERS")}
            value={tempSettings.tracking_min_distance || ''}
          />

          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            id="cancel_distance_threshold"
            name="cancel_distance_threshold"
            onChange={handleCancelDistanceThreshold}
            label={t("MIN_DISTANCE_TO_CANCEL_METERS")}
            helperText={t("MIN_DISTANCE_TO_CANCEL_HELPER")}
            value={tempSettings.cancel_distance_threshold || ''}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Typography component="h1" variant="h5">
            {t('advance_settings')}
          </Typography>
          <FormControlLabel
            style={{ marginTop: '10px' }}
            control={
              <Switch
                checked={tempSettings.horizontal_view || false}
                onChange={handleSwitchChange}
                name="horizontal_view"
                color="primary"
              />
            }
            label={t('car_view_horizontal')}
          />

          <FormControlLabel
            style={{ marginTop: '10px' }}
            control={
              <Switch
                checked={tempSettings.prepaid || false}
                onChange={handleSwitchChange}
                color="primary"
                name="prepaid"
              />
            }
            label={t('ESTIMATED_FARE_WILL_ALWAYS_BE_THE_FARE_FOR_THE_RIDE')}
          />

          <FormControlLabel
            style={{ marginTop: '10px' }}
            control={
              <Switch
                color="primary"
                onChange={handleSwitchChange}
                name="users_contacts_not_shared_in_races"
                checked={tempSettings.users_contacts_not_shared_in_races || false}
              />
            }
            label={t('USERS_CONTACTS_NOT_SHARED_IN_RACES')}
          />

          <FormControlLabel
            style={{ marginTop: '10px' }}
            control={
              <Switch
                color="primary"
                onChange={handleSwitchChange}
                name="NOT_SHOW_THE_HOURLY_PRICE_FOR_PASSENGER_CAR_CATEGORIES"
                checked={tempSettings.NOT_SHOW_THE_HOURLY_PRICE_FOR_PASSENGER_CAR_CATEGORIES || false}
              />
            }
            label={t('NOT_SHOW_THE_HOURLY_PRICE_FOR_PASSENGER_CAR_CATEGORIES')}
          />

          <Typography component="h1" variant="h5" style={{ marginTop: '15px' }}>
            {t("PASSENGER_SETTINGS")}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.passenger_allow_negative_balance || false}
                name="passenger_allow_negative_balance"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t("ALLOW_PASSENGERS_TO_RIDE_WITH_A_NEGATIVE_BALANCE")}
          />

          {!tempSettings.passenger_allow_negative_balance && (
            <TextField
              fullWidth
              margin="normal"
              variant="outlined"
              id="max_passenger_negative_balance"
              name="max_passenger_negative_balance"
              onChange={handleMaxPassengerNegativeBalance}
              autoComplete="max_passenger_negative_balance"
              label={t("MAXIMUM_NEGATIVE_BALANCE_THAT_CAN_BE_BLOCKED")}
              value={tempSettings.max_passenger_negative_balance || ''}
            />
          )}

          <Typography component="h1" variant="h5" style={{ marginTop: '15px' }}>
            {t('driver_setting')}
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            id="driverRadius"
            variant="outlined"
            name="driverRadius"
            label={t('driverRadius')}
            autoComplete="driverRadius"
            onChange={handleDriverRadius}
            value={tempSettings.driverRadius || ''}
          />
          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.negativeBalance || false}
                onChange={handleSwitchChange}
                name="negativeBalance"
                color="primary"
              />
            }
            label={t("ALLOW_THE_DRIVER_TO_OPERATE_THE_VEHICLE_WITH_A_NEGATIVE_CASH_BALANCE")}
          />

          {!tempSettings.negativeBalance && (
            <TextField
              fullWidth
              margin="normal"
              variant="outlined"
              id="maxNegativeBalance"
              name="maxNegativeBalance"
              onChange={handleNegativeBalance}
              autoComplete="maxNegativeBalance"
              value={tempSettings.maxNegativeBalance || ''}
              label={t("MAXIMUM_NEGATIVE_BALANCE_THAT_CAN_BE_BLOCKED")}
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.license_image_required || false}
                onChange={handleSwitchChange}
                name="license_image_required"
                color="primary"
              />
            }
            label={t('license_image_required')}
          />

          <Typography component="h1" variant="h5" style={{ marginTop: '15px' }}>
            {t('cars')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.required_car_image_frente || false}
                name="required_car_image_frente"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('front_photo')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.required_car_image_lateraldireita || false}
                name="required_car_image_lateraldireita"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('right_side_photo')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.required_car_image_lateralesquerda || false}
                name="required_car_image_lateralesquerda"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('left_side_photo')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.required_car_image_traseira || false}
                name="required_car_image_traseira"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('rear_photo')}
          />

          <FormControlLabel
            control={
              <Switch
                checked={tempSettings.required_car_image_documento || false}
                name="required_car_image_documento"
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('crlv_photo')}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="center" padding={5}>
        <Button
          startIcon={<SaveIcon />}
          onClick={handleSave}
          variant="contained"
          color="primary"
          size='large'
          fullWidth
        >
          {t('save')}
        </Button>
      </Box>
    </Grid>
  );
}

export default GeneralSettings;